import type {
  DocumentComment,
  DocumentSuggestion,
} from '@/api/docfiller-types'

/**
 * Local view-model for a comment thread: the root row + replies in the
 * order the server returned them. Computed once in `groupReview()` so
 * the panel and the overlay both iterate the same structure.
 */
export interface CommentThread {
  root: DocumentComment
  replies: DocumentComment[]
}

export interface ReviewItems {
  threads: CommentThread[]
  /** Threads grouped by anchor page so the overlay can index by page. */
  threadsByPage: Map<number, CommentThread[]>
  /** Pending suggestions surfaced as overlay pins. */
  pendingSuggestions: DocumentSuggestion[]
  pendingByPage: Map<number, DocumentSuggestion[]>
  /** Resolved + pending suggestions, in creation order, for the panel list. */
  allSuggestions: DocumentSuggestion[]
}

/**
 * Build the thread/page indexes shared by `ReviewPanel` and
 * `ReviewOverlay`. Pure on the inputs so it's safe to memoize with
 * `useMemo` against the raw query results.
 */
export function groupReview(
  comments: DocumentComment[],
  suggestions: DocumentSuggestion[],
): ReviewItems {
  const byUuid = new Map<string, DocumentComment>()
  for (const c of comments) byUuid.set(c.uuid, c)
  const roots: DocumentComment[] = []
  const replyMap = new Map<string, DocumentComment[]>()
  for (const c of comments) {
    if (c.parent_comment_uuid && byUuid.has(c.parent_comment_uuid)) {
      const bucket = replyMap.get(c.parent_comment_uuid) ?? []
      bucket.push(c)
      replyMap.set(c.parent_comment_uuid, bucket)
    } else {
      roots.push(c)
    }
  }
  const threads = roots.map<CommentThread>((root) => ({
    root,
    replies: replyMap.get(root.uuid) ?? [],
  }))
  const threadsByPage = new Map<number, CommentThread[]>()
  for (const t of threads) {
    const page = t.root.anchor.page
    const bucket = threadsByPage.get(page) ?? []
    bucket.push(t)
    threadsByPage.set(page, bucket)
  }

  const pendingSuggestions = suggestions.filter((s) => s.status === 'pending')
  const pendingByPage = new Map<number, DocumentSuggestion[]>()
  for (const s of pendingSuggestions) {
    // Suggestions without an explicit anchor land on the field's page if
    // we know it (via `field_uuid`), or page 1 otherwise — the panel is
    // the source of truth for resolution, the overlay is just a hint.
    const page = s.anchor?.page ?? 1
    const bucket = pendingByPage.get(page) ?? []
    bucket.push(s)
    pendingByPage.set(page, bucket)
  }

  return {
    threads,
    threadsByPage,
    pendingSuggestions,
    pendingByPage,
    allSuggestions: suggestions,
  }
}
